import React, { useCallback, useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";
import ErrorBoundary from "@amzn/awsui-components-react/polaris/error-boundary";
import {
  Box,
  Button,
  Drawer,
  Header,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  DrawerRequest,
  InvestigationDrawer,
} from "../investigations/InvestigationDrawer";
import {
  investigationPath,
  investigationsPagePath,
  parseQuickSightCallback,
  parseQuickSightCallbackTransactionId,
} from "../investigations/investigationUtils";
import { QuickSightDashboard } from "./QuicksightDashboard";
import { InvestigationsApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import {
  GO_TO_INVESTIGATION_ACTION_ID,
  callbackActionId,
  parseQuickSightCallbackInvestigationId,
} from "./rowActions";
import { DashboardStageSelector } from "./DashboardStageSelector";
import PageHeader from "../PageHeader";
import TrackStarsLaunch from "../common/TrackStarsLaunch";
import DarkModeContext from "../../contexes/DarkModeContext";
import { StageContext } from "../../contexes/OrderIdContext";
import { AnalyticsContext } from "./AnalyticsProvider";
import { DashboardViewSelection } from "./SavedViewsManager";
import { Stage } from "../../lusca-search-api/generated-src";
import { DARK_THEME } from "../../constants";
import useDocumentTitle from "../../hooks/useDocumentTitle";

// Last width (px) the user dragged the investigations drawer to.
const DRAWER_WIDTH_STORAGE_KEY = "lusca.dlqInvestigationsDrawer.width";

const Dashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [darkMode, setDarkMode] = useState(
    () => window.localStorage.getItem("theme") === DARK_THEME
  );
  const stageParam = (searchParams.get("stage") as Stage) || Stage.Prod;
  const [shareHandler, setShareHandler] = useState<
    (() => Promise<string>) | null
  >(null);
  const [filtersHandler, setFiltersHandler] = useState<
    (() => Promise<string>) | null
  >(null);
  const [filterRequest, setFilterRequest] = useState<
    | {
        filters: string | null;
        ts: number;
      }
    | undefined
  >();
  const [activeViewName, setActiveViewName] = useState<string | null>(
    searchParams.get("view") || null
  );
  const [activeDrawerId, setActiveDrawerId] = useState<string | null>(
    searchParams.get("investigations") === "open" ||
      searchParams.get("investigate")
      ? "investigations"
      : null
  );
  // The drawer hosts tables and forms, so it opens at ~45% of the viewport (never narrower than
  // 640px) rather than Cloudscape's 290px default. A width the user drags to is remembered in
  // localStorage and wins over the computed default on later visits.
  const [drawerDefaultSize] = useState(() => {
    try {
      const saved = Number(
        window.localStorage.getItem(DRAWER_WIDTH_STORAGE_KEY)
      );
      if (Number.isFinite(saved) && saved >= 200) return Math.round(saved);
    } catch {
      // localStorage unavailable (privacy mode etc.) — fall back to the computed default.
    }
    return Math.min(
      1200,
      Math.max(
        640,
        Math.round(
          (typeof window !== "undefined" ? window.innerWidth : 1440) * 0.45
        )
      )
    );
  });
  // ?investigate=<transaction or order ID> (from a transaction's DLQ deep dive) opens the drawer
  // straight on the lookup for that ID; the param is one-shot so a later Back to the queue sticks.
  const [drawerRequest, setDrawerRequest] = useState<DrawerRequest | undefined>(
    () => {
      const transactionId = searchParams.get("investigate");
      return transactionId ? { kind: "txn", transactionId } : undefined;
    }
  );

  // Never replace the whole search-param set: filters, view and share links must survive.
  const updateSearchParams = (mutate: (params: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next);
  };

  const openDrawer = useCallback((request: DrawerRequest) => {
    setDrawerRequest(request);
    setActiveDrawerId("investigations");
  }, []);

  const openInvestigationTab = useCallback(
    (investigationId: string) => {
      const url = investigationPath(investigationId, stageParam);
      // No "noopener" feature string: with it window.open always returns null, which would hide
      // a blocked pop-up. Sever the opener by hand instead, and navigate here if it was blocked.
      const tab = window.open(url, "_blank");
      if (tab) tab.opener = null;
      else window.location.assign(url);
    },
    [stageParam]
  );

  const handleQuickSightCallback = useCallback(
    (message: unknown) => {
      // "6. Go to Investigation (new tab)" opens the row's case in a new tab. It uses the row's
      // `investigation_id` when the visual carries that column; otherwise (most row tables don't
      // show it) it asks Lusca live who owns the row's dlq_pk. Only a row nobody owns opens the
      // drawer, where an investigation can be started (QuickSight shows the entry on every row).
      if (callbackActionId(message) === GO_TO_INVESTIGATION_ACTION_ID) {
        const investigationId = parseQuickSightCallbackInvestigationId(message);
        if (investigationId) {
          openInvestigationTab(investigationId);
          return;
        }
        const rowSeed = parseQuickSightCallback(message);
        if (rowSeed) {
          InvestigationsApiFactory()
            .resolveInvestigationRow(stageParam, { dlqPk: rowSeed.dlqPk })
            .then(({ data }) => {
              const owner = data.primary?.id;
              if (owner) openInvestigationTab(owner);
              else openDrawer({ kind: "row", seed: rowSeed });
            })
            .catch((err) => {
              console.error("Could not look up the row's investigation:", err);
              openDrawer({ kind: "row", seed: rowSeed });
            });
          return;
        }
      }
      const seed = parseQuickSightCallback(message);
      if (seed) {
        openDrawer({ kind: "row", seed });
        return;
      }
      // The row visual may not expose dlq_pk; a transaction/order ID still gets the user straight
      // to the matching DLQ items instead of an empty form.
      const transactionId = parseQuickSightCallbackTransactionId(message);
      if (transactionId) {
        openDrawer({ kind: "txn", transactionId });
        return;
      }
      console.warn(
        "QuickSight callback without dlq_pk or transaction_id datapoints",
        message
      );
      openDrawer({ kind: "manual" });
    },
    [openDrawer, openInvestigationTab, stageParam]
  );

  const handleEnvironmentChange = (environment: string) => {
    updateSearchParams((params) => {
      params.set("stage", environment);
      params.delete("view");
      params.delete("filters");
    });
    setActiveViewName(null);
  };

  const handleLoadView = (view: DashboardViewSelection | null) => {
    if (!view) {
      setActiveViewName(null);
      setFilterRequest({ filters: null, ts: Date.now() });
      return;
    }
    setActiveViewName(view.name);
    if ("stage" in view && view.stage !== stageParam) {
      updateSearchParams((params) => params.set("stage", view.stage));
    }
    // Built-in views use complete, known-good snapshots just like saved views.
    setFilterRequest({ filters: view.filters, ts: Date.now() });
  };

  const { recordError } = useContext(AnalyticsContext);
  useDocumentTitle("DLQ Dashboard");

  return (
    <ErrorBoundary
      errorBoundaryId="dashboard"
      i18nStrings={{
        headerText: "Unexpected error, dashboard failed to display",
        descriptionText: "Refresh the page to try again.",
        refreshActionText: "Refresh page",
      }}
      onError={({ error, errorInfo }) => {
        console.log("Caught error: ", error, errorInfo);
        recordError(error);
      }}
    >
      <DarkModeContext.Provider value={darkMode}>
        <StageContext.Provider value={stageParam}>
          <PageHeader setDarkMode={setDarkMode} />
          <AppLayout
            content={
              <SpaceBetween size="m">
                <Box padding={{ horizontal: "l", top: "m" }}>
                  <DashboardStageSelector
                    stage={stageParam}
                    setStage={handleEnvironmentChange}
                    getShareUrl={shareHandler}
                    getCompressedFilters={filtersHandler}
                    onLoadView={handleLoadView}
                    activeViewName={activeViewName}
                    setActiveViewName={setActiveViewName}
                    centerSlot={<TrackStarsLaunch product="dlq" />}
                    investigationActions={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button
                          iconName="search"
                          onClick={() => openDrawer({ kind: "manual" })}
                          ariaLabel="Investigate a DLQ item by primary key (keyboard accessible alternative to the dashboard row action)"
                        >
                          Investigate DLQ item
                        </Button>
                        <Button
                          iconName="external"
                          iconAlign="right"
                          href={investigationsPagePath(stageParam)}
                          target="_blank"
                          ariaLabel="Open the DLQ investigations work queue in a new tab"
                        >
                          Investigations
                        </Button>
                      </SpaceBetween>
                    }
                  />
                </Box>
                <QuickSightDashboard
                  stage={stageParam}
                  dashboardType="dlq"
                  onCallback={handleQuickSightCallback}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  setShareHandler={setShareHandler}
                  setFiltersHandler={setFiltersHandler}
                  filterRequest={filterRequest}
                  activeViewName={activeViewName}
                />
              </SpaceBetween>
            }
            contentType="default"
            headerSelector="#h"
            maxContentWidth={1700}
            navigationHide
            drawers={[
              {
                id: "investigations",
                content: (
                  <Drawer
                    header={
                      <Header
                        variant="h2"
                        actions={
                          <Button
                            href={investigationsPagePath(stageParam)}
                            iconName="external"
                            iconAlign="right"
                            target="_blank"
                            ariaLabel="Open the DLQ investigations page in a new tab"
                          >
                            Open full page
                          </Button>
                        }
                      >
                        DLQ investigations
                      </Header>
                    }
                  >
                    <InvestigationDrawer
                      stage={stageParam}
                      request={drawerRequest}
                      hideWorkQueue
                    />
                  </Drawer>
                ),
                trigger: { iconName: "status-in-progress" },
                resizable: true,
                defaultSize: drawerDefaultSize,
                onResize: ({ detail }) => {
                  try {
                    window.localStorage.setItem(
                      DRAWER_WIDTH_STORAGE_KEY,
                      String(Math.round(detail.size))
                    );
                  } catch {
                    // Ignore storage failures; resizing still works for this session.
                  }
                },
                ariaLabels: {
                  drawerName: "DLQ investigations",
                  closeButton: "Close investigations",
                  triggerButton: "Open investigations",
                  resizeHandle: "Resize investigations drawer",
                },
              },
            ]}
            activeDrawerId={activeDrawerId}
            onDrawerChange={({ detail }) =>
              setActiveDrawerId(detail.activeDrawerId)
            }
          />
        </StageContext.Provider>
      </DarkModeContext.Provider>
    </ErrorBoundary>
  );
};

export default Dashboard;
